// packages/ui/src/api-core.ts
// Общее API-ядро для b2b и lk (admin — отдельный, см. apps/admin/src/lib/api.ts)
// Содержит: withToken (retry+refresh), fetchWithToken, auth-методы, extractToken
import {
  AuthService,
  configureApi,
  setToken,
  getToken,
  setRefreshToken,
  getRefreshToken,
  refreshAccessToken,
  clearAuth,
} from '@tb/api-client/client';
import { reportClientError } from './error-reporter';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Извлекает имя ошибки и stack из исключения */
function describeError(e: any): { name: string; message: string; stack?: string } {
  if (e instanceof Error) return { name: e.name, message: e.message, stack: e.stack };
  if (typeof e === 'string') return { name: 'Error', message: e };
  // OpenAPI client errors: { status, body: { message } }
  const status = e?.status ?? e?.statusCode;
  const bodyMsg = e?.body?.message ?? e?.message;
  return {
    name: status ? `ApiError${status}` : 'ApiError',
    message: bodyMsg ?? JSON.stringify(e).slice(0, 200),
  };
}

/** Единый wrapper: токен → 401 refresh → retry → transient retry → auto-report */
export const withToken =
  <Args extends any[], R>(fn: (...a: Args) => Promise<R>) =>
  async (...a: Args): Promise<R> => {
    configureApi(getToken() ?? undefined);
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        return await fn(...a);
      } catch (e: any) {
        // 401 — пытаемся refresh
        if (e?.status === 401) {
          const newToken = await refreshAccessToken();
          if (newToken) {
            configureApi(newToken);
            try {
              return await fn(...a);
            } catch {}
          }
        }
        // 400/401/403/404/409/422 — ошибка клиента: ретрай бессмыслен и жжёт лимит запросов
        if ([400, 401, 403, 404, 409, 422].includes(e?.status)) {
          const { name, message, stack } = describeError(e);
          reportClientError({
            severity: 'warning',
            name,
            message,
            stack,
            context: { source: 'withToken', statusCode: e?.status },
          });
          throw e;
        }
        if (attempt < 1) {
          await sleep(1500);
          configureApi(getToken() ?? undefined);
          continue;
        }
        // Финальная ошибка → репортим (кроме 429 rate-limit и 304 Not Modified)
        if (e?.status !== 429 && e?.status !== 304) {
          const { name, message, stack } = describeError(e);
          reportClientError({
            severity: e?.status >= 500 ? 'fatal' : 'error',
            name,
            message,
            stack,
            context: { source: 'withToken', statusCode: e?.status },
          });
        }
        throw e;
      }
    }
    throw new Error('unreachable');
  };

const BASE = () => process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4000';

/** Universal fetch с auto-refresh на 401 — для эндпоинтов вне @tb/api-client */
export async function fetchWithToken(path: string, opts: RequestInit = {}): Promise<any> {
  const doFetch = (token: string | null) =>
    fetch(`${BASE()}${path}`, {
      ...opts,
      headers: {
        // Content-Type только при наличии тела: Fastify даёт 400 на DELETE с
        // json-заголовком и пустым телом ("Body cannot be empty...")
        ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...opts.headers,
      },
    });

  try {
    let res = await doFetch(getToken());
    if (res.status === 401) {
      const newToken = await refreshAccessToken();
      if (newToken) {
        res = await doFetch(newToken);
      }
    }
    const json = await res.json();
    if (!res.ok) {
      // Репортим API-ошибки (4xx/5xx), кроме 429 rate-limit и 304 Not Modified
      if (res.status !== 429 && res.status !== 304) {
        reportClientError({
          severity: res.status >= 500 ? 'fatal' : 'warning',
          name: `ApiError${res.status}`,
          message: json?.message ?? `HTTP ${res.status} ${path}`,
          context: { source: 'fetchWithToken', path, method: opts.method ?? 'GET', statusCode: res.status },
        });
      }
      throw { body: json, status: res.status };
    }
    return json;
  } catch (e: any) {
    // Network error ("Failed to fetch") — репортим
    if (e?.message?.includes('Failed to fetch') || e?.name === 'TypeError') {
      reportClientError({
        severity: 'error',
        name: 'NetworkError',
        message: e?.message ?? 'Failed to fetch',
        stack: e?.stack,
        context: { source: 'fetchWithToken', path, method: opts.method ?? 'GET' },
      });
    }
    throw e;
  }
}

// ===== AUTH =====
export const auth = {
  login: async (email: string, password: string) => {
    configureApi();
    const r: any = await AuthService.authControllerLogin({ requestBody: { email, password } });
    const { accessToken, refreshToken } = r?.data ?? {};
    if (accessToken) {
      setToken(accessToken);
      if (refreshToken) setRefreshToken(refreshToken);
    }
    return r;
  },
  register: (email: string, password: string, firstName?: string, lastName?: string) =>
    withToken(AuthService.authControllerRegister)({ requestBody: { email, password, firstName, lastName } }),
  me: () => withToken(AuthService.authControllerMe)(),
  updateProfile: (body: { firstName?: string; lastName?: string; avatarUrl?: string; gender?: string; birthDate?: string }) =>
    fetchWithToken('/api/auth/me', { method: 'PATCH', body: JSON.stringify(body) }),
  logout: async () => {
    const rt = getRefreshToken();
    clearAuth();
    try {
      await withToken(AuthService.authControllerLogout)({ requestBody: { refreshToken: rt ?? '' } });
    } catch {
      /* ignore */
    }
  },
  changePassword: (currentPassword: string, newPassword: string) =>
    withToken(AuthService.authControllerChangePassword)({ requestBody: { currentPassword, newPassword } }),
  forgotPassword: (email: string) =>
    withToken(AuthService.authControllerForgotPassword)({ requestBody: { email } }),
  resetPassword: (token: string, newPassword: string) =>
    withToken(AuthService.authControllerResetPassword)({ requestBody: { token, newPassword } }),
};

/** Толерантное извлечение токена из ответа */
export function extractToken(res: any): string | null {
  if (!res) return null;
  return (
    res?.data?.accessToken ??
    res?.accessToken ??
    res?.data?.data?.accessToken ??
    res?.data?.token ??
    res?.token ??
    null
  );
}

export { setToken, getToken, setRefreshToken, getRefreshToken, clearAuth, configureApi };
